<script>
    // Licensed under Gnu Public Licence version 2
    // Copyright (c) 2023-2024 (original work) Open Assessment Technologies SA ;
    import { createPopper } from '@popperjs/core';
    import { createEventDispatcher, tick } from 'svelte';
    export let reference;
    export let placement = 'bottom';
    export let modifiers = [];
    export let strategy = 'absolute';
    export let isOpen = false;
    export let trigger = 'hover';

    const eventListeners = new Map();

    const dispatch = createEventDispatcher();

    let flyoutRoot;
    let showFlyout = isOpen;
    let keepAlive = false;

    async function handleOpenStateChange(newState) {
        if (newState) {
            showFlyout = newState;
            // allow the template and it's bindings to be updated
            await tick();
            const popper = createPopper(reference, flyoutRoot, {
                placement,
                modifiers: [
                    {
                        name: 'flip',
                        options: {
                            fallbackPlacements: ['top', 'bottom']
                        }
                    },
                    ...modifiers
                ],
                strategy
            });
            dispatch('show', popper);
        } else {
            setTimeout(() => {
                showFlyout = newState;
            }, 0);
        }
    }

    function handleTriggerChange(newTrigger, referenceEl, flyoutRootEl) {
        for (const [[element, event], listener] of eventListeners) {
            element?.removeEventListener(event, listener);
        }
        eventListeners.clear();
        switch (newTrigger) {
            case 'hover':
                eventListeners.set([referenceEl, 'mouseenter'], () => {
                    isOpen = true;
                });
                eventListeners.set([referenceEl, 'mouseleave'], () => {
                    isOpen = false;
                });
                eventListeners.set([flyoutRootEl, 'mouseenter'], () => {
                    keepAlive = true;
                });
                eventListeners.set([flyoutRootEl, 'mouseleave'], () => {
                    keepAlive = false;
                });
                break;
            case 'click':
                eventListeners.set([referenceEl, 'click'], (evt) => {
                    evt.stopPropagation();
                    isOpen = !isOpen;
                });
                eventListeners.set([flyoutRootEl, 'click'], (evt) => {
                    evt.stopPropagation();
                });
                eventListeners.set([document, 'click'], () => {
                    isOpen = false;
                });
                break;
            case 'focus':
                eventListeners.set([referenceEl, 'focus'], () => {
                    isOpen = true;
                });
                eventListeners.set([referenceEl, 'blur'], () => {
                    isOpen = false;
                });
                break;
        }
        for (const [[element, event], listener] of eventListeners) {
            element?.addEventListener(event, listener);
        }
    }

    $: handleOpenStateChange(isOpen);
    $: handleTriggerChange(trigger, reference, flyoutRoot);
</script>

<style>
    .flyout {
        display: inline-flex;
        flex-direction: column;
        align-items: flex-start;
        border: 1px solid var(--color-bg-inverted-secondary-hover, hsl(0, 0%, 55%));
        background: var(--color-bg-default);
        box-shadow: 0px 2px 4px 0px rgba(0, 0, 0, 0.25);
    }
</style>

{#if showFlyout || keepAlive}
    <div
        bind:this={flyoutRoot}
        class="flyout"
        {...$$restProps}>
        <slot />
    </div>
{/if}
